import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { type CustomProduct } from '@repair/catalog/types/common/product';

import { LevelType, type Process, type ILinkProduct } from '../apis/ilink.type';
import { type Brand } from '../apis/dictionaries.type';
import { amiProductId } from '../business/amiProductId';
import { PRODUCT_NATURE_TYPES } from '../constants/nature.const';
import { type FallbackUrlParams } from '../utils/hooks/useProductSheetUrlParams';

export interface StoredProductSheetUrlParams extends FallbackUrlParams {
  productLabel?: string;
  baseProcessId?: string;
  baseCategoryId?: string;
  isEditingProduct?: boolean;
}

export interface BreadcrumbLevel {
  level: LevelType;
  label: string | undefined;
  id?: number | null;
  hide?: boolean;
}

interface CatalogSearch {
  breadcrumb: BreadcrumbLevel[];
  product: ILinkProduct;
  defaultImageUrl?: string;
  fetchNeeded: boolean;
  translatedMessage?: string;
  initialBreadCrumbLevel?: BreadcrumbLevel;
}

interface CatalogState {
  search: CatalogSearch;
  productSheetUrlParams?: StoredProductSheetUrlParams;
}

export const initialProductCatalog = {
  articles: [],
  id: amiProductId,
  label: '',
  brand: null,
  process: { id: null },
  category: { id: null },
  nature: { id: -1, label: '', image: '', type: PRODUCT_NATURE_TYPES.product },
  groupNatureLevelTwo: { id: -1, label: '', image: '' },
  groupNatureLevelThree: { id: -1, label: '', image: '' },
  isCustom: null,
};

export const initialCustomProductCatalog: CustomProduct = { ...initialProductCatalog, isCustom: true, articles: [] as [], imageUrl: '' };

const initialState: CatalogState = {
  search: {
    breadcrumb: [],
    product: { ...initialProductCatalog },
    defaultImageUrl: undefined,
    fetchNeeded: true,
  },
  productSheetUrlParams: undefined,
};

const mapProduct = (product: ILinkProduct): ILinkProduct => {
  const id = product.id ?? amiProductId;
  return {
    ...product,
    id,
    imageUrl: product.imageUrl ?? initialState.search.defaultImageUrl,
    isCustom: product.isCustom,
  };
};

const catalogSlice = createSlice({
  name: 'catalog',
  initialState,
  extraReducers: builder => builder.addCase('resetStore', () => initialState),
  reducers: {
    updateCatalogSearch: (state, action: PayloadAction<CatalogSearch>) => {
      state.search = {
        ...action.payload,
        product: mapProduct(action.payload.product),
      };
    },
    resetCatalogSearch: state => {
      state.search = {
        ...state.search,
        breadcrumb: [{ ...state.search.breadcrumb[0], hide: false }],
        fetchNeeded: true,
      };
    },
    updateProduct: (state, action: PayloadAction<ILinkProduct>) => {
      state.search = {
        ...state.search,
        product: mapProduct(action.payload),
      };
    },
    resetProduct: state => ({
      ...state,
      search: {
        ...state.search,
        product: initialProductCatalog,
      },
    }),
    updateBreadcrumb: (state, action: PayloadAction<BreadcrumbLevel[]>) => {
      state.search = {
        ...state.search,
        breadcrumb: action.payload,
      };
    },
    updateCatalogProcess: (state, action: PayloadAction<Process>) => {
      const { id, label } = action.payload;
      state.search = {
        ...state.search,
        breadcrumb: [
          { ...state.search.breadcrumb[0], hide: false },
          {
            level: LevelType.categories,
            label,
            id,
          },
        ],
        fetchNeeded: true,
      };
    },
    updateTraceabilityNumber: (state, action: PayloadAction<string>) => {
      state.search = {
        ...state.search,
        product: {
          ...state.search.product,
          traceabilityNumber: action.payload,
        },
      };
    },
    updateProductBrand: (state, action: PayloadAction<Brand | null>) => {
      state.search = {
        ...state.search,
        product: {
          ...state.search.product,
          brand: action.payload,
        },
      };
    },
    updateProductLabel: (state, action: PayloadAction<string>) => {
      state.search = {
        ...state.search,
        product: {
          ...state.search.product,
          label: action.payload,
        },
      };
    },
    updateProductSheetUrlParams: (state, action: PayloadAction<StoredProductSheetUrlParams>) => {
      state.productSheetUrlParams = { ...state.productSheetUrlParams, ...action.payload };
    },
    resetProductSheetUrlParams: state => {
      state.productSheetUrlParams = undefined;
    },
  },
});

export const {
  reducer: catalogReducer,
  actions: {
    resetCatalogSearch,
    updateCatalogSearch,
    updateProduct,
    resetProduct,
    updateBreadcrumb,
    updateCatalogProcess,
    updateTraceabilityNumber,
    updateProductBrand,
    updateProductLabel,
    updateProductSheetUrlParams,
    resetProductSheetUrlParams,
  },
} = catalogSlice;
